// TODO: Update tsconfig paths to remove the need for '/index'. This will be resolved with `.feature` files (export menu, routes, permissions, feature flags, etc.).

import { createBrowserRouter } from 'react-router-dom';

import { accessControlRoutes } from '@features/access-control/index';
import { AccountRoutes } from '@features/account/index';
import { AssetDataServiceRoutes } from '@features/assets/index';
import { ConfigurationRulesRoutes } from '@features/configuration-rules/index';
import { ContactsRoutes } from '@features/contacts/index';
import {
  CollaboratorsRoutes,
  CyberResilienceAssessmentRoutes,
  FileRepositoryRoutes,
  UpApiProvider,
} from '@features/cyber-resilience-assessment/index';
import { dashboardRoutes } from '@features/dashboard-legacy/index';
import { dataExplorerRoutes } from '@features/data-explorer/index';
import { DetectionCatalogRoutes } from '@features/detection-catalog/index';
import { EscalationsRoutes } from '@features/escalations/index';
import { IncidentResponseRoutes } from '@features/incident-response/index';
import { insightRoutes } from '@features/insights/index';
import { logDownloadRoutes } from '@features/log-download/index';
import { OnboardingRoutes } from '@features/onboarding/index';
import { ReportsRoutes } from '@features/reports/index';
import { ResourceCenterRoutes } from '@features/resource-center/index';
import { RiskManagementRoutes } from '@features/risk-management/index';
import { securityJourneyRoutes } from '@features/security-journey/index';
import { TelemetryRoutes } from '@features/telemetry/index';
import { threatIntelSkuRoutes } from '@features/threat-intel-sku/index';
import { TicketsRoutes } from '@features/tickets/index';
import { UnifiedContentLibraryRoutes } from '@features/unified-content-library/index';
import { FullScreenLoader } from '@packages/components/loader/full-screen-loader';
import { ProtectedRoute } from '@packages/components/protected-route/protected-route';
import { SadWolfError } from '@packages/components/sadWolfError/SadWolfError';
import { RequireSelectedOrganizationGuard } from '@packages/core/components/require-selected-organization-guard/require-selected-organization-guard';
import { CoreRoutes } from '@packages/core/pages/core.routes';
import OrganizationSwitchPage from '@packages/core/pages/organization-switch/OrganizationSwitch.page';
import { STORAGE_KEYS } from 'ae-constants/constants';
import { ORGANIZATION_SWITCH_ROUTE } from 'ae-constants/routes.constants';
import { Organizations } from 'ae-kernel/organizations/organizations';

import { App } from '../app/app';
import { Main } from '../app/main/main';

import { redirectionRoutes } from './redirection-routes';

// REFACTORME: Move OrganizationSwitchPage in the router of Core to be able to lazy load the page
// const OrganizationSwitchPage = lazy(
//   () =>
//     import('@packages/core/pages/organization-switch/OrganizationSwitch.page'),
// );

// NOTICE - the below variable is dynamically modified by the
// @aw/ui-platform/unified-portal-feature NX generator; please contact
// #rnd_icebreakers if you are refactoring this code
const importedRoutes = [
  dashboardRoutes,
  dataExplorerRoutes,
  insightRoutes,
  securityJourneyRoutes,
  logDownloadRoutes,
  threatIntelSkuRoutes,
  accessControlRoutes,
];

export const browserRouter: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    {
      children: [
        ...redirectionRoutes,
        {
          children: [
            {
              children: [
                ...importedRoutes,
                {
                  path: '/account',
                  children: [
                    {
                      path: 'contacts/*',
                      element: <ContactsRoutes />,
                    },
                    {
                      path: 'escalations/*',
                      element: <EscalationsRoutes />,
                    },
                    {
                      path: 'configuration-rules/*',
                      element: <ConfigurationRulesRoutes />,
                    },
                    {
                      path: 'onboarding/*',
                      element: <OnboardingRoutes />,
                    },
                    {
                      path: '*',
                      element: <AccountRoutes />,
                    },
                  ],
                },
                {
                  element: <ResourceCenterRoutes />,
                  path: '/resource-center/*',
                },
                { element: <AssetDataServiceRoutes />, path: '/assets/*' },
                {
                  element: <DetectionCatalogRoutes />,
                  path: '/detection-catalog/*',
                },
                {
                  children: [
                    {
                      element: <RiskManagementRoutes />,
                      path: '/risk-management/*',
                    },
                  ],
                  element: (
                    <ProtectedRoute
                      legacyCapabilities={{ canAccessManageRisk: true }}
                    />
                  ),
                },
                {
                  children: [
                    { element: <ReportsRoutes />, path: '/reports/*' },
                  ],
                  element: (
                    <ProtectedRoute
                      legacyCapabilities={{ canViewAllTickets: true }}
                    />
                  ),
                },
                {
                  element: <IncidentResponseRoutes />,
                  path: '/incident-response/*',
                },
                { element: <TicketsRoutes />, path: '/tickets/*' },
                { element: <TelemetryRoutes />, path: '/telemetry/*' },
                {
                  element: <UnifiedContentLibraryRoutes />,
                  path: '/unified-content-library/*',
                },
                {
                  element: (
                    <UpApiProvider>
                      <CyberResilienceAssessmentRoutes />
                    </UpApiProvider>
                  ),
                  path: '/security-journey/cyber-resilience-assessment/*',
                },
                {
                  element: (
                    <UpApiProvider>
                      <CollaboratorsRoutes />
                    </UpApiProvider>
                  ),
                  path: '/security-journey/collaborators/*',
                },
                {
                  element: (
                    <UpApiProvider>
                      <FileRepositoryRoutes />
                    </UpApiProvider>
                  ),
                  path: '/security-journey/files/*',
                },
              ],
              element: (
                <Organizations
                  waitForRendallUrl
                  loadingElement={<FullScreenLoader />}
                  errorElement={<SadWolfError />}
                >
                  <Main />
                </Organizations>
              ),
            },
          ],
          element: <RequireSelectedOrganizationGuard />,
        },
        {
          children: [
            {
              element: <OrganizationSwitchPage />,
              loader: () => {
                window.localStorage.removeItem(STORAGE_KEYS.AWN_ORGANIZATION);
                return Promise.resolve(null);
              },
              path: ORGANIZATION_SWITCH_ROUTE,
            },
            { element: <CoreRoutes />, path: '*' },
          ],
          element: (
            <Organizations
              waitForRendallUrl={false}
              loadingElement={<FullScreenLoader />}
              errorElement={<SadWolfError />}
            >
              <Main />
            </Organizations>
          ),
        },
      ],
      element: <App />,
    },
  ]);
